/**
 * Main application entry piont.
 * This function does application initialization and root components rendering.
 */
const runApplication = () => {
    runPreContentLoadInitialization();

    // make sure DOM is ready
    if (Sephora.DOMContentLoadedFired) {
        renderApplication();
    } else {
        window.addEventListener('DOMContentLoaded', renderApplication);
    }

    runServicesInitialization();
};

/**
 * App initialization step #1.
 *
 * Any pre-initialization code should be placed in here.
 */
const runPreContentLoadInitialization = () => {
    const Application = require('utils/framework/Application');
    const { Immediate, EventType } = require('constants/events');

    // The Immediate event fires once all priority code is loaded but prior to any third party or api dependencies.
    Application.events.dispatchServiceEvent(Immediate, EventType.Ready);
    Application.events.dispatchServiceEvent(Immediate, EventType.ServiceCtrlrsApplied, true);

    // Analytics - This needs to run before any controllers do, so that the promise exists ahead of time
    /**
     * Wait for our initial page load tag to have fired before firing any subsequent tags.
     * All subsequent async page load tags depend on the vars set in this tag.
     * Resolve: Called from Signal tag "Global :: Page-load Tracking", Additional Configuration
     */
    Sephora.analytics.promises.initialPageLoadFired = new Promise(function (resolve) {
        Sephora.analytics.resolvePromises.initialPageLoadFired = resolve;
    });

    // Place all async rendered components in Sephora.Comps.
    // Initialize component inflation logic, e.g. ApplyCtrlr
    // Require.ensure inPageList.js which will add <script src="compnents.chunk.js"> to the page
    require('utils/framework/InflateComponents');

    const Performance = require('utils/framework/performance/Performance');
    Performance.initialize();
};

/**
 * App initialization step #2.
 */
const runPostContentLoadInitialization = () => {
    const cookieUtils = require('utils/Cookies');
    cookieUtils.initialize();

    const Store = require('store/Store');
    Store.initialize();
};

/**
 * Render root components
 */
const renderApplication = () => {
    runPostContentLoadInitialization();

    const { renderRootComponents } = require('utils/framework/InflateSPA');
    renderRootComponents()
        .then(runPostHydrationInitialization)
        .catch(error => {
            if (error) {
                // eslint-disable-next-line no-console
                console.log(JSON.stringify(error));
            }
        });
};

/**
 * App initialization step #3.
 *
 * To avoid store mutations before hydration process has finished it's work we need to do all store updates only
 * after first render circle because it's mandatory to render same HTML markup as it was after SSR.
 * Do all initialization store mutations from here because this function is invoked right after hydration.
 */
const runPostHydrationInitialization = () => {
    const Store = require('store/Store');
    Store.onHydrationFinished();

    const History = require('services/History');
    History.initFrontEndRouter();

    const CookieActions = require('actions/CookieActions');
    Store.dispatch(CookieActions.setAllCookies(Sephora.Util.InflatorComps.pageLoadCookies));
};

/**
 * App initialization step #4.
 *
 * It may run before initialization step #2 (`runPostContentLoadInitialization`) when `DOMContentLoaded` event has not fired yet.
 * Initialize the third-party and internal services.
 * These are listed in order of priority.
 * Generally speaking each of these hooks up to any third party JS files needed and or requests expected data from the third party service.
 */
const runServicesInitialization = () => {
    require('services/UserInfo');
    require('services/LithiumHeaderImage');
    require('services/Search');
    require('services/Catalog');
    require('services/RefreshBasket');
    require('services/Certona')();
    require('services/TestTarget').initialize();
    require('services/productInfo').initialize();

    // require.ensure is used to package nested requires in components.chunk.js
    // rather than priority.bundle.js in order to keep priority.bundle.js lean
    require.ensure(
        [],
        function (require) {
            require('services/Location');
            require('services/OrderInfo');
            require('services/PostLoad');
            require('services/ThirdParty');
        },
        'components'
    );
};

module.exports = { runApplication };
